import { InteractionType } from "@azure/msal-browser";
import { useIsAuthenticated } from "@azure/msal-react";
import { ViewStack } from "@pomle/react-viewstack";
import { Fade } from "@pomle/react-viewstack-transitions";
import { PropsWithChildren, useCallback, useEffect } from "react";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";
import { useConfig } from "render/context/ConfigContext";
import {
  locale2lang,
  useInternationalization,
} from "render/context/InternationalizationContext";
import { LogoView } from "render/views/LogoView";
import { useMSAL, useMsalAuthentication } from "../../MSALContext";
import { AuthenticatedProps } from "../../types";

export function Authenticated({
  children,
  ErrorView,
}: PropsWithChildren<AuthenticatedProps>) {
  const { locale } = useInternationalization();
  const config = useConfig();
  const isAuthenticated = useIsAuthenticated();
  const { session, isReady: isMsalReady } = useMSAL();
  const { trackException } = useAppInsights();
  const scopes = config.appConfig.msal.scopes.token;

  const { error, login } = useMsalAuthentication(InteractionType.Redirect, {
    scopes,
    redirectUri: window.location.origin,
    extraQueryParameters: {
      ui_locales: locale2lang(locale),
    },
  });
  const loginCallback = useCallback(() => {
    return login(InteractionType.Redirect, {
      scopes,
      redirectUri: window.location.origin,
    });
  }, [login, scopes]);

  useEffect(() => {
    if (!error) {
      return;
    }

    trackException({ exception: error, severityLevel: SeverityLevel.Error });
  }, [error, trackException]);

  useEffect(() => {
    // Means the ID token has expired and we need to re-login
    if (error?.errorCode === "invalid_grant") {
      login();
    }
  }, [error?.errorCode, login]);

  // if Id token has expired, we don't want to show an error screen
  if (error && error.errorCode === "invalid_grant") {
    return null;
  }

  if (error) {
    return <ErrorView login={loginCallback} error={error} />;
  }

  const isReady = !!(isAuthenticated && session && isMsalReady);

  return (
    <ViewStack>
      {isReady && <>{children}</>}
      <Fade active={!isReady}>
        <LogoView />
      </Fade>
    </ViewStack>
  );
}
